import Axios from "axios";
import { Loading, MessageBox } from "element-ui";
import { localStorage } from "@/utils/localStorage";
import { clearBrowserData } from "@/utils/common";
import i18nLocale from "@/helpers/locale";
import router from "@/router";
import qs from "qs";
import errMsgMapping from "./errMsgMapping";

localStorage.set("requestQueue", []);
localStorage.set("hasTriggerAuthInvalid", false);

let loadClose = null;
let isDefaultAlertTips = true;

const errMapping = {
  // TOKEN_EXPIRED: "token_expired",
  // AUTH_FAIL: "auth_fail",
  // PW_EXPIRED: "password_expired",
  NO_PERMISSION: "no_permission",
  SERVER_ERROR: "server_error",
  NOT_FOUND_LOGINDATA: "not_found_loginData", // means token_expired?
};

const axios = Axios.create({
  timeout: 1000 * 60 * 3,
  // timeout: 1000 * 3,
  withCredentials: true,
  credentials: "include",
  headers: {
    "Access-Control-Allow-Credentials": true,
    "Content-Type": "application/json;charset=utf-8",
  },
  paramsSerializer: params => qs.stringify(params, { indices: false }),
});

const loadingMask = () => {
  loadClose = Loading.service({
    text: `${i18nLocale("global.loading")}...`,
    spinner: "el-icon-loading",
    background: "rgba(0, 0, 0, 0.5)"
  });
};

const promptFn = (text, callback = () => {}) => {
  MessageBox.alert(text, {
    showClose: false,
    callback,
    customClass: "message-box-with-line-break"
  });
};

const handleTokenExpired = () => {
  // eslint-disable-next-line no-console
  console.log("Token expired, Please login again!");
  const hasTriggerAuthInvalid = localStorage.get("hasTriggerAuthInvalid");
  clearBrowserData();

  if (!hasTriggerAuthInvalid) {
    localStorage.set("hasTriggerAuthInvalid", true);
    promptFn(i18nLocale("err.authInvalid"), () => {
      router.push({ path: "/" }, () => {
        localStorage.set("hasTriggerAuthInvalid", false);
      });
    });
  }
};

const handleNoPermission = () => {
  promptFn(i18nLocale("err.noPermission"));
};

const handleException = ({ status = "", error, message }) => {
  const statusCode = status.toString();
  const serverCodeReg = /^50\d+/;
  const feCodeReg = /^40\d+/;
  let errText = i18nLocale("err.networkError");

  if (serverCodeReg.test(statusCode)) {
    errText = message || i18nLocale("err.500Error");

    if (error) {
      switch (error) {
      case errMapping.AUTH_FAIL:
        errText = i18nLocale("err.usernamePwNotMatch");
        break;
      case errMapping.NO_PERMISSION:
        handleNoPermission();
        return;
      case errMapping.NOT_FOUND_LOGINDATA:
        handleTokenExpired();
        return;
      case errMapping.PW_EXPIRED:
        return;
      default:
        break;
      }
    }
  } else if (feCodeReg.test(statusCode)) {
    if (status === 401) {
      handleTokenExpired();
      return;
    }
    errText = i18nLocale("err.400Error");
  }

  promptFn(errText);
};

const removeRequest = () => {
  const existingRequest = localStorage.get("requestQueue") || [];
  if (existingRequest.length > 0) {
    existingRequest.shift();
  }
  localStorage.set("requestQueue", existingRequest);

  return existingRequest;
};

axios.interceptors.request.use(
  config => {
    config.headers.Authorization = localStorage.get("token");

    const existingRequest = localStorage.get("requestQueue") || [];
    const stockRequest = [...existingRequest, config.url];
    localStorage.set("requestQueue", stockRequest);

    let params = config.data.data;
    let isLoading = typeof config.data.loading === "boolean" ? config.data.loading : true; // Defaut to show loading
    let _loginAsAccountId = localStorage.get("loginAsAccountId");

    if (_loginAsAccountId) {
      config.headers.changeAccountId = _loginAsAccountId;
    }

    if (config.data.isDefaultAlertTips === false) {
      isDefaultAlertTips = false;
    } else {
      isDefaultAlertTips = true;
    }

    if (isLoading) { loadingMask(); }

    // config.data = { ...params };
    config.data = params;

    const configMethod = config.method.toLowerCase();

    switch (configMethod) {
    case "uploadfile":
      config.method = "post";
      config.headers["Content-Type"] = "multipart/form-data";
      break;
    case "get":
      config.params = { ...params };
      break;
    default:
      break;
    }

    return config;
  },
  error => {
    if (loadClose) { loadClose.close(); }
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  response => {
    const existingRequest = removeRequest();
    if (loadClose && existingRequest.length === 0) { loadClose.close(); }

    const { message, messageCode } = response.data;
    if ((message && message !== null || messageCode) && isDefaultAlertTips) {
      const _msg = messageCode ? errMsgMapping(messageCode) : message;
      promptFn(_msg);
    }

    return Promise.resolve(response.data);
  },
  ({ response, sysMsg }) => {
    const existingRequest = removeRequest();
    if (loadClose && existingRequest.length === 0) { loadClose.close(); }

    if (response && isDefaultAlertTips) {
      const { status, data: { message, error } } = response;
      handleException({ status, error, message: message || sysMsg });
    }
    return Promise.reject(response);
  }
);

export default axios;

export const requestAll = async requestMethods => {
  if (!requestMethods) {
    // eslint-disable-next-line no-console
    console.error("Please pass the request function here.");
    return;
  }

  loadingMask();

  try {
    const res = await Axios.all([ ...requestMethods ]);
    return await Promise.resolve(res);
  } catch (err) {
    if (err) {
      const { status, data: { message, error } = {} } = err;
      handleException({ status, error, message });
    }

    return await Promise.reject(err);
  }
};